import React, { useState, useEffect } from "react";
import API_URL from "../Config";
import axiosInstance from "../axiosinstance";
import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  Divider,
} from "@mui/material";

const formatDateDisplay = (str) => {
  if (!str) return "N/A";
  try {
    const date = new Date(str);
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  } catch {
    return str;
  }
};

const Row = ({ label, value }) => (
  <div style={{ display: "flex", gap: "12px", padding: "8px 0" }}>
    <Typography
      variant="body2"
      sx={{ color: "#64748b", minWidth: "160px", fontWeight: 500 }}
    >
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: "#1e293b" }}>
      {value || "N/A"}
    </Typography>
  </div>
);

const ViewProjectDetail = ({ open, onClose, projectId }) => {
  const [project, setProject] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !projectId) return;
    
    const fetchData = async () => {
      try {
        setLoading(true);
        const [projectRes, usersRes] = await Promise.all([
          axiosInstance.get(`${API_URL}/api/get_project/${projectId}`),
          axiosInstance.get(`${API_URL}/api/get_users_for_dropdown`),
        ]);
        setProject(projectRes.data?.data || null);
        const normalizedUsers = (usersRes.data?.user_data || []).map((user) => ({
          ...user,
          users_name: user.user_name || "",
        }));
        setUsers(normalizedUsers);
      } catch (err) {
        console.error("Failed to fetch project details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, projectId]);

  const handleClose = () => {
    setProject(null);
    setUsers([]);
    onClose();
  };

  const getUserName = (userId) => {
    const user = users.find((u) => u.user_id?.trim() === userId?.trim());
    return user?.users_name?.trim() || userId;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Project Details
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {loading && (
          <Typography variant="body2" sx={{ color: "#94a3b8", py: 2 }}>
            Loading...
          </Typography>
        )}

        {!loading && project && (
          <>
            {/* Basic Info */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Project Information
            </Typography>
            <Row label="Project Name" value={project.name} />
            <Row label="Project Code" value={project.project_code} />
            <Row label="Work Order ID" value={project.work_order_id} />
            <Row label="Unit Name" value={project.unit_name} />
            <Row label="Description" value={project.description} />
            <Row label="Start Date" value={formatDateDisplay(project.start_date)} />
            <Row label="End Date" value={formatDateDisplay(project.end_date)} />

            <Divider sx={{ my: 2 }} />

            {/* Client Info */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Client Information
            </Typography>
            <Row label="Client Code" value={project.client_code} />
            <Row label="Client Name" value={project.client_name} />
            <Row label="Location" value={project.location} />
            <Row label="Timezone" value={project.timezone} />

            <Divider sx={{ my: 2 }} />

            {/* Users In-Charge */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Users In-Charge
            </Typography>
            {project.user_project?.length ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {project.user_project.map((u) => (
                  <Box
                    key={u.user_id}
                    sx={{
                      p: 1.5,
                      border: "1px solid #e2e8f0",
                      borderRadius: 1.5,
                      backgroundColor: "#f8fafc",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: 1,
                    }}
                  >
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {getUserName(u.user_id)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "#64748b" }}>
                      {formatDateDisplay(u.access_start)} →{" "}
                      {formatDateDisplay(u.access_end)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No users assigned
              </Typography>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Rating Configuration */}
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: "#475569", mb: 1 }}
            >
              Rating Configuration
            </Typography>
            {project.rating_config?.length ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {project.rating_config.map((config, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      border: "1px solid #e2e8f0",
                      borderRadius: 1.5,
                      backgroundColor: "#f8fafc",
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                    }}
                  >
                    <Typography variant="body2">
                      <span style={{ color: "#64748b" }}>Value: </span>
                      {config.rating_value || "N/A"}
                    </Typography>
                    <Typography variant="body2">
                      <span style={{ color: "#64748b" }}>Type: </span>
                      {config.type || "N/A"}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <span style={{ color: "#64748b", fontSize: "14px" }}>
                        Color:
                      </span>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "4px",
                          backgroundColor: config.color_code || "#000",
                          border: "1px solid #e2e8f0",
                        }}
                      />
                      <Typography variant="body2">
                        {config.color_code || "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No rating configuration
              </Typography>
            )}
          </>
        )}

        {!loading && !project && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No project data found.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ViewProjectDetail;
